import { IntelligentScoringService } from "./intelligentScoring";
import { MedicalAnalysisService } from "./medicalAnalysis";
import { ICD11Service, ICD11Entry } from "./icd11Service";
import { MedicalSearchService } from "./medicalSearch";

interface FollowUpPlan {
  timeline: string;
  checkPoints: FollowUpCheckPoint[];
  escalationTriggers: string[];
  monitoringFocus: string[];
  intelligentRecommendations: string[];
  evidenceBased: boolean;
  confidenceLevel: number;
}

interface FollowUpCheckPoint {
  timeFrame: string;
  description: string;
  action: 'self_monitor' | 'contact_provider' | 'seek_urgent_care';
  questions: string[];
  intelligentPrompts: string[];
  symptomSpecific: boolean;
}

export class FollowUpPlanningService {
  /**
   * Generate intelligent follow-up plan based on actual medical analysis and scoring
   */
  async generateFollowUpPlan(
    aiScore: number, 
    category: string, 
    symptoms: string[],
    urgency: 'routine' | 'scheduled' | 'prompt' | 'urgent',
    answers?: Record<string, any>,
    medicalEvidence?: any[]
  ): Promise<FollowUpPlan> {
    
    try {
      // Get intelligent medical analysis for follow-up planning
      const [medicalAnalysis, icd11Conditions, followUpResearch] = await Promise.all([
        MedicalAnalysisService.getEnhancedAnalysis(symptoms, aiScore / 10, answers?.symptom_duration || ''),
        this.getRelevantConditionsForFollowUp(symptoms, category),
        MedicalSearchService.searchRelevantKnowledge(symptoms, [], ['follow_up', 'monitoring', 'prognosis'], 10)
      ]);

      // Generate dynamic follow-up plan based on actual medical data
      return this.generateIntelligentFollowUpPlan(
        aiScore,
        symptoms,
        urgency,
        medicalAnalysis,
        icd11Conditions,
        followUpResearch,
        answers
      );

    } catch (error) {
      console.error('Error generating intelligent follow-up plan:', error);
      return this.generateConservativeFollowUpPlan(aiScore, symptoms, urgency);
    }
  }

  private async getRelevantConditionsForFollowUp(symptoms: string[], category: string): Promise<ICD11Entry[]> {
    try {
      const field = this.mapCategoryToField(category);
      const icd11Response = await ICD11Service.mapResponsesToICD11({
        field,
        symptoms,
        duration: 'ongoing',
        pattern: 'monitoring',
        impact: 5
      });

      return [...icd11Response.primaryConditions, ...icd11Response.secondaryConditions];
    } catch (error) {
      console.error('Error fetching follow-up conditions:', error);
      return [];
    }
  }

  private async generateIntelligentFollowUpPlan(
    aiScore: number,
    symptoms: string[],
    urgency: string,
    medicalAnalysis: any,
    icd11Conditions: ICD11Entry[],
    research: any[],
    answers?: Record<string, any>
  ): Promise<FollowUpPlan> {
    
    // Generate dynamic timeline based on medical evidence
    const timeline = this.calculateIntelligentTimeline(aiScore, symptoms, medicalAnalysis, answers);
    
    // Generate intelligent checkpoints
    const checkPoints = await this.generateIntelligentCheckPoints(
      aiScore,
      symptoms,
      urgency,
      medicalAnalysis,
      icd11Conditions,
      answers
    );

    // Generate evidence-based escalation triggers
    const escalationTriggers = this.generateIntelligentEscalationTriggers(
      symptoms,
      icd11Conditions,
      research,
      aiScore
    );

    // Generate dynamic monitoring focus areas
    const monitoringFocus = this.generateIntelligentMonitoringFocus(
      symptoms,
      icd11Conditions,
      medicalAnalysis,
      answers
    );

    // Generate intelligent recommendations
    const intelligentRecommendations = this.generateIntelligentRecommendations(
      medicalAnalysis,
      research,
      aiScore
    );

    return {
      timeline,
      checkPoints,
      escalationTriggers,
      monitoringFocus,
      intelligentRecommendations,
      evidenceBased: true,
      confidenceLevel: this.calculatePlanConfidence(medicalAnalysis, research, icd11Conditions)
    };
  }

  // Helper methods for intelligent planning

  private mapCategoryToField(category: string): string {
    const mapping: Record<string, string> = {
      'Head and Nerves': 'head/neurological',
      'Respiratory': 'chest/breathing',
      'Digestive': 'stomach/digestion',
      'Musculoskeletal': 'muscle/bones',
      'Skin': 'skin',
      'Heart and Blood': 'cardiovascular',
      'Mental Health': 'mental health'
    };
    return mapping[category] || 'general';
  }

  private generateConservativeFollowUpPlan(
    aiScore: number, 
    symptoms: string[], 
    urgency: string
  ): FollowUpPlan {
    return {
      timeline: 'Monitor for 24-48 hours initially',
      checkPoints: [{
        timeFrame: '24-48 hours',
        description: 'Initial monitoring period',
        action: 'self_monitor',
        questions: ['Are symptoms improving?', 'Any new concerns?'],
        intelligentPrompts: ['Track symptom changes', 'Note any triggers'],
        symptomSpecific: false
      }],
      escalationTriggers: ['Symptoms worsen', 'New symptoms develop'],
      monitoringFocus: ['Overall symptom progression', 'Daily function impact'],
      intelligentRecommendations: ['Continue monitoring', 'Consider medical evaluation if no improvement'],
      evidenceBased: false,
      confidenceLevel: 0.6
    };
  }

  private calculateIntelligentTimeline(
    aiScore: number,
    symptoms: string[],
    medicalAnalysis: any,
    answers?: Record<string, any>
  ): string {
    const duration = answers?.symptom_duration || '';
    const dailyImpact = parseInt(answers?.daily_impact) || 5;
    
    if (aiScore >= 80) {
      return 'Immediate to 24 hours - seek urgent care';
    } else if (aiScore >= 60) {
      return '24-48 hours initial assessment, then weekly monitoring';
    } else if (aiScore >= 40) {
      return '2-3 days initial monitoring, then weekly check-ins';
    } else if (duration.includes('chronic') || dailyImpact >= 7) {
      return '3-5 days monitoring, then bi-weekly assessment';
    } else {
      return '3-7 days monitoring with self-assessment';
    }
  }

  private async generateIntelligentCheckPoints(
    aiScore: number,
    symptoms: string[],
    urgency: string,
    medicalAnalysis: any,
    icd11Conditions: ICD11Entry[],
    answers?: Record<string, any>
  ): Promise<FollowUpCheckPoint[]> {
    const checkPoints: FollowUpCheckPoint[] = [];
    
    // Dynamic checkpoint generation based on score and evidence
    if (aiScore >= 80) {
      checkPoints.push({
        timeFrame: 'Immediately',
        description: 'Seek emergency medical attention',
        action: 'seek_urgent_care',
        questions: this.generateUrgentQuestions(symptoms),
        intelligentPrompts: this.generateUrgentPrompts(icd11Conditions),
        symptomSpecific: true
      });
    } else if (aiScore >= 60) {
      checkPoints.push({
        timeFrame: '24-48 hours',
        description: 'Medical evaluation recommended',
        action: 'contact_provider',
        questions: this.generatePromptQuestions(symptoms, medicalAnalysis),
        intelligentPrompts: this.generatePromptPrompts(symptoms, icd11Conditions),
        symptomSpecific: true
      });
    } else if (aiScore >= 40) {
      checkPoints.push({
        timeFrame: '2-3 days',
        description: 'Schedule medical consultation',
        action: 'contact_provider',
        questions: this.generateScheduledQuestions(symptoms),
        intelligentPrompts: this.generateScheduledPrompts(symptoms),
        symptomSpecific: true
      });
    } else {
      checkPoints.push({
        timeFrame: '3-7 days',
        description: 'Self-monitoring with scheduled check-in',
        action: 'self_monitor',
        questions: this.generateRoutineQuestions(symptoms),
        intelligentPrompts: this.generateRoutinePrompts(symptoms),
        symptomSpecific: true
      });
    }

    // Add follow-up checkpoints based on medical analysis
    if (medicalAnalysis.recommendations.length > 0) {
      checkPoints.push({
        timeFrame: this.calculateFollowUpTimeframe(aiScore),
        description: 'Reassessment based on medical evidence',
        action: aiScore >= 40 ? 'contact_provider' : 'self_monitor',
        questions: this.generateEvidenceBasedQuestions(medicalAnalysis),
        intelligentPrompts: this.generateEvidenceBasedPrompts(medicalAnalysis),
        symptomSpecific: true
      });
    }

    return checkPoints;
  }

  private generateIntelligentEscalationTriggers(
    symptoms: string[],
    icd11Conditions: ICD11Entry[],
    research: any[],
    aiScore: number
  ): string[] {
    const triggers: string[] = [];
    
    // Base escalation triggers
    triggers.push('Symptoms worsen significantly instead of improving');
    
    // Symptom-specific triggers
    symptoms.forEach(symptom => {
      const symptomLower = symptom.toLowerCase();
      if (symptomLower.includes('pain')) {
        triggers.push(`${symptom} becomes severe or unmanageable`);
      } else if (symptomLower.includes('breathing')) {
        triggers.push('Breathing difficulties worsen or new chest symptoms develop');
      } else if (symptomLower.includes('headache')) {
        triggers.push('Headache becomes sudden, severe, or changes pattern');
      }
    });

    // ICD-11 condition specific triggers
    icd11Conditions.forEach(condition => {
      if (condition.title.toLowerCase().includes('cardiac')) {
        triggers.push('Any chest pain, shortness of breath, or palpitations');
      } else if (condition.title.toLowerCase().includes('neurological')) {
        triggers.push('New neurological symptoms or confusion');
      }
    });

    // Research-based triggers
    const emergencyKeywords = ['emergency', 'urgent', 'immediate', 'hospital'];
    research.forEach(study => {
      const content = (study.content || '').toLowerCase();
      emergencyKeywords.forEach(keyword => {
        if (content.includes(keyword) && content.includes('if')) {
          // Extract emergency conditions from research
          triggers.push(`Consider urgent care if symptoms align with research findings`);
        }
      });
    });

    // Score-based triggers
    if (aiScore >= 30) {
      triggers.push('No improvement after 24-48 hours of monitoring');
    } else {
      triggers.push('No improvement after 5-7 days of self-care');
    }

    return triggers.slice(0, 6); // Limit to most relevant triggers
  }

  private generateIntelligentMonitoringFocus(
    symptoms: string[],
    icd11Conditions: ICD11Entry[],
    medicalAnalysis: any,
    answers?: Record<string, any>
  ): string[] {
    const focus: string[] = [];
    
    // Base monitoring
    focus.push('Overall symptom trajectory and severity changes');
    focus.push('Impact on daily functioning and quality of life');
    
    // Symptom-specific monitoring
    symptoms.forEach(symptom => {
      const symptomLower = symptom.toLowerCase();
      if (symptomLower.includes('pain')) {
        focus.push(`${symptom} intensity, location, and triggering factors`);
      } else if (symptomLower.includes('fatigue')) {
        focus.push('Energy levels and sleep quality patterns');
      } else if (symptomLower.includes('mood') || symptomLower.includes('anxiety')) {
        focus.push('Mental health symptoms and coping strategies effectiveness');
      }
    });

    // ICD-11 condition specific monitoring
    icd11Conditions.forEach(condition => {
      if (condition.symptom_mappings) {
        condition.symptom_mappings.forEach(mapping => {
          focus.push(`Monitor for ${mapping.toLowerCase()} as indicated by ${condition.title}`);
        });
      }
    });

    // Medical analysis based monitoring
    if (medicalAnalysis.recommendations) {
      medicalAnalysis.recommendations.forEach((rec: string) => {
        if (rec.toLowerCase().includes('monitor')) {
          focus.push(`Track progress as recommended: ${rec.toLowerCase()}`);
        }
      });
    }

    return focus.slice(0, 8); // Return most relevant monitoring areas
  }

  private generateIntelligentRecommendations(
    medicalAnalysis: any,
    research: any[],
    aiScore: number
  ): string[] {
    const recommendations: string[] = [];
    
    // Medical analysis recommendations
    if (medicalAnalysis.recommendations) {
      recommendations.push(...medicalAnalysis.recommendations.slice(0, 3));
    }
    
    // Research-based recommendations
    const selfCareKeywords = ['self-care', 'home treatment', 'lifestyle', 'management'];
    research.forEach(study => {
      const content = (study.content || study.summary || '').toLowerCase();
      selfCareKeywords.forEach(keyword => {
        if (content.includes(keyword)) {
          recommendations.push(`Consider evidence-based approaches from ${study.title}`);
        }
      });
    });

    // Score-based recommendations
    if (aiScore >= 60) {
      recommendations.push('Prioritize medical evaluation given elevated risk assessment');
    } else if (aiScore >= 40) {
      recommendations.push('Schedule routine medical consultation for comprehensive evaluation');
    } else {
      recommendations.push('Continue self-monitoring with scheduled reassessment');
    }

    return recommendations.slice(0, 5);
  }

  private calculatePlanConfidence(
    medicalAnalysis: any,
    research: any[],
    icd11Conditions: ICD11Entry[]
  ): number {
    let confidence = 0.5; // Base confidence
    
    if (medicalAnalysis.evidenceLevel === 'high') confidence += 0.2;
    else if (medicalAnalysis.evidenceLevel === 'medium') confidence += 0.1;
    
    if (research.length >= 5) confidence += 0.15;
    else if (research.length >= 2) confidence += 0.1;
    
    if (icd11Conditions.length >= 3) confidence += 0.1;
    else if (icd11Conditions.length >= 1) confidence += 0.05;
    
    return Math.min(0.95, confidence);
  }

  // Helper methods for generating intelligent prompts and questions
  
  private generateUrgentQuestions(symptoms: string[]): string[] {
    return [
      'Have you contacted emergency services or are you in a safe location?',
      'Are your symptoms rapidly worsening or becoming unmanageable?',
      'Do you have someone with you for support and assistance?'
    ];
  }

  private generateUrgentPrompts(conditions: ICD11Entry[]): string[] {
    const prompts = ['Seek immediate medical attention'];
    conditions.forEach(condition => {
      if (condition.severity_level === 'high') {
        prompts.push(`Monitor for signs consistent with ${condition.title}`);
      }
    });
    return prompts;
  }

  private generatePromptQuestions(symptoms: string[], medicalAnalysis: any): string[] {
    const questions = [
      'Have you been able to schedule a medical appointment within 24-48 hours?',
      'Are your symptoms remaining stable or showing signs of improvement?'
    ];
    
    symptoms.forEach(symptom => {
      questions.push(`How has your ${symptom.toLowerCase()} changed since yesterday?`);
    });
    
    return questions.slice(0, 4);
  }

  private generatePromptPrompts(symptoms: string[], conditions: ICD11Entry[]): string[] {
    const prompts = ['Schedule medical evaluation promptly'];
    
    conditions.forEach(condition => {
      prompts.push(`Track symptoms related to ${condition.title}`);
    });
    
    return prompts.slice(0, 3);
  }

  private generateScheduledQuestions(symptoms: string[]): string[] {
    return [
      'Have you contacted your healthcare provider to schedule an appointment?',
      'Are you managing your symptoms effectively with current measures?',
      'Have any new symptoms developed that concern you?'
    ];
  }

  private generateScheduledPrompts(symptoms: string[]): string[] {
    return [
      'Schedule medical consultation within appropriate timeframe',
      'Continue monitoring and documenting symptom patterns',
      'Note any changes or new developments'
    ];
  }

  private generateRoutineQuestions(symptoms: string[]): string[] {
    return [
      'Are your symptoms improving with self-care measures?',
      'Are you able to maintain your normal daily activities?',
      'Have you noticed any patterns or triggers for your symptoms?'
    ];
  }

  private generateRoutinePrompts(symptoms: string[]): string[] {
    return [
      'Continue with appropriate self-care measures',
      'Monitor for any changes in symptom pattern or severity',
      'Consider medical evaluation if no improvement after one week'
    ];
  }

  private calculateFollowUpTimeframe(aiScore: number): string {
    if (aiScore >= 60) return '1 week';
    else if (aiScore >= 40) return '2 weeks';
    else return '2-3 weeks';
  }

  private generateEvidenceBasedQuestions(medicalAnalysis: any): string[] {
    const questions = [
      'Are you following the evidence-based recommendations from your initial assessment?',
      'Have you seen improvement as expected based on medical evidence?'
    ];
    
    if (medicalAnalysis.recommendations) {
      questions.push('Are there any challenges in implementing the recommended approaches?');
    }
    
    return questions;
  }

  private generateEvidenceBasedPrompts(medicalAnalysis: any): string[] {
    const prompts = ['Continue evidence-based monitoring'];
    
    if (medicalAnalysis.evidenceLevel === 'high') {
      prompts.push('High-quality evidence supports current monitoring approach');
    }
    
    return prompts;
  }

  // Legacy method for backward compatibility
  getSymptomTrackingQuestions(category: string, symptoms: string[]): string[] {
    const baseQuestions = [
      'Rate your overall symptom severity from 1-10',
      'How are your symptoms affecting your daily activities?',
      'Have you noticed any patterns or triggers?',
      'Are symptoms better, worse, or the same as yesterday?'
    ];

    // Generate intelligent, symptom-specific questions
    const intelligentQuestions: string[] = [];
    
    symptoms.forEach(symptom => {
      const symptomLower = symptom.toLowerCase();
      if (symptomLower.includes('pain')) {
        intelligentQuestions.push(`Rate your ${symptom.toLowerCase()} from 1-10 and describe its character`);
      } else if (symptomLower.includes('mood')) {
        intelligentQuestions.push(`How has your ${symptom.toLowerCase()} affected your daily interactions?`);
      } else {
        intelligentQuestions.push(`How has your ${symptom.toLowerCase()} changed since yesterday?`);
      }
    });

    return [...baseQuestions, ...intelligentQuestions].slice(0, 6);
  }
}

export const followUpPlanningService = new FollowUpPlanningService();